import React from "react";
import { useTranslation } from "react-i18next";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  BonusTranslationPayload,
  setTranslationLocally,
} from "store/liveOps/bonuses/slice";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import TextEditor from "components/wysiwyg/Wysiwyg";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { ImageUpload } from "components/imageUpload";

interface Props {
  activeLocale: string;
  setActiveLocale: (e: string) => void;
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  isReadOnly?: boolean;
}

const BonusesContentTab: React.FC<Props> = ({
  activeLocale,
  setActiveLocale,
  fieldErrors,
  clearFieldError,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const localesList = useAppSelector((store) => store.settings.locales);
  const translation = useAppSelector(
    (store) => store.bonuses.translations?.[activeLocale],
  );

  const handleFieldChange = (
    key: keyof BonusTranslationPayload,
    value: string | number | boolean | File,
  ) => {
    if (isReadOnly || !translation) return;

    dispatch(
      setTranslationLocally({
        locale: activeLocale,
        data: {
          ...translation,
          locale: activeLocale,
          [key]: value,
        },
      }),
    );
  };

  return (
    <div className="tournament-rules">
      <div className="game-modal-tabs bonus-currencies-tabs small">
        {localesList?.map((el) => (
          <div
            className={`tab-item ${activeLocale === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setActiveLocale(el);
            }}
          >
            <img
              src={`https://flagpedia.net/data/flags/icon/16x12/${el
                .split("-")[1]
                ?.toLowerCase()}.png`}
              alt=""
            />
            {t(el)}
          </div>
        ))}
      </div>
      <div className="tournament-rules-form">
        {!translation ? (
          <ModalTabSkeleton />
        ) : (
          <div className="bonus-form-content bonus-main-form-content bonus-locales-form-content">
            <div className="bonus-form-box bonus-locales-text-fields">
              <div className="bonus-locales-title-row">
                <div className="bonus-form-box--item">
                  <InnerInput
                    value={translation?.title ?? ""}
                    placeholder={t("enterTitle")}
                    label={t("title")}
                    type="text"
                    id="bonus-translation-title"
                    disabled={isReadOnly || !activeLocale}
                    onChange={(e) => {
                      handleFieldChange("title", e);
                      clearFieldError("title");
                    }}
                    hasError={Boolean(fieldErrors?.["title"])}
                  />
                </div>

                <div className="bonus-form-switcher bonus-locales-active">
                  <div className="title">{t("active")}</div>
                  <SwitchBtn
                    isActive={!!translation?.isActive}
                    disabled={isReadOnly}
                    handleClick={() =>
                      handleFieldChange(
                        "isActive",
                        translation?.isActive ? 0 : 1,
                      )
                    }
                  />
                </div>
              </div>
              <div className="bonus-form-box--item">
                <InnerInput
                  maxCodePoints={2500}
                  value={translation?.description ?? ""}
                  placeholder={t("enterText")}
                  label={t("description")}
                  type="textarea"
                  id="bonus-translation-description"
                  disabled={isReadOnly || !activeLocale}
                  onChange={(e) => {
                    handleFieldChange("description", e);
                    clearFieldError("description");
                  }}
                  hasError={Boolean(fieldErrors?.["description"])}
                />
              </div>
            </div>

            <div className="bonus-form-box bonus-locales-media-fields">
              <div className="bonus-form-img">
                <div className="bonus-images">
                  <div className="bonus-image-item desktop">
                    <ImageUpload
                      label="Desktop"
                      labelClassName="bonus-main-form-label"
                      image={translation?.image}
                      allowMp4
                      size="large"
                      onAddImage={(file) => {
                        handleFieldChange("image", file);
                        clearFieldError("image");
                      }}
                      isActive={true}
                      disabled={isReadOnly}
                      hasError={!!fieldErrors?.["image"]}
                    />
                  </div>

                  <div className="bonus-images-secondary">
                    <div className="bonus-image-item mobile">
                      <ImageUpload
                        label="Mobile"
                        labelClassName="bonus-main-form-label"
                        image={translation?.mobileImage}
                        allowMp4
                        size="large"
                        type="landscape"
                        aspect="21:9"
                        onAddImage={(file) => {
                          handleFieldChange("mobileImage", file);
                          clearFieldError("mobileImage");
                        }}
                        isActive={true}
                        disabled={isReadOnly}
                        hasError={!!fieldErrors?.["mobileImage"]}
                      />
                    </div>

                    <div className="bonus-image-item bonus-portrait">
                      <ImageUpload
                        label="Portrait"
                        labelClassName="bonus-main-form-label"
                        image={translation?.portraitImage}
                        allowMp4
                        size="large"
                        type="portrait"
                        aspect="9:16"
                        onAddImage={(file) => {
                          handleFieldChange("portraitImage", file);
                          clearFieldError("portraitImage");
                        }}
                        isActive={true}
                        disabled={isReadOnly}
                        hasError={!!fieldErrors?.["portraitImage"]}
                      />
                    </div>
                </div>
              </div>
            </div>
            </div>

            <div className="bonus-form-box--item html-editor bonus-locales-content-editor">
              <div className="label">{t("content")}</div>
              <TextEditor
                value={translation?.content ?? ""}
                disabled={isReadOnly || !activeLocale}
                onChange={(e) => {
                  handleFieldChange("content", e);
                  clearFieldError("content");
                }}
                hasError={Boolean(fieldErrors?.["content"])}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BonusesContentTab;
